"use client";

import type { ReactNode } from "react";
import { useId } from "react";

/**
 * CSS variable overrides for white-labeling.
 *
 * Keys should be CSS variable names (e.g., '--color-action-brand'). Legacy brand keys are still supported.
 * Values should be valid CSS values (e.g., '#ff6600', 'rgb(255, 102, 0)').
 *
 * Common brand variables:
 * - --color-action-brand: Primary brand color
 * - --color-action-brand-hover: Darker brand variant for hover states
 * - --color-brand-primary: Legacy primary brand color alias
 * - --color-brand-dark: Legacy darker brand color alias
 *
 * @example
 * {
 *   '--color-action-brand': '#ff6600',
 *   '--color-action-brand-hover': '#cc5200',
 * }
 */
export type BrandVariables = Record<string, string>;

export interface BrandProviderProps {
  /**
   * CSS variable overrides to apply for white-labeling.
   * These override the default Edges theme variables.
   */
  variables?: BrandVariables;
  /**
   * CSS variable overrides to apply when dark mode is active (`.theme-dark`).
   * Uses the same cascade map as `variables` — overriding `--color-action-brand`
   * automatically cascades to derived tokens (action colors, link colors, etc.).
   *
   * When omitted, the dark mode theme falls back to Edges defaults.
   * This is a non-breaking addition — existing consumers are unaffected.
   *
   * @example
   * ```tsx
   * <BrandProvider
   *   variables={{ '--color-action-brand': '#2563eb' }}
   *   darkVariables={{ '--color-action-brand': '#60a5fa' }}
   * >
   *   <App />
   * </BrandProvider>
   * ```
   */
  darkVariables?: BrandVariables;
  children: ReactNode;
}

/**
 * Mapping of brand variables to their derived semantic tokens.
 * When a brand variable is overridden, all its derived tokens are also set.
 *
 * This is needed because CSS custom property inheritance doesn't re-resolve
 * var() references defined at :root when a descendant overrides the base variable.
 */
const BRAND_CASCADE_MAP: Record<string, string[]> = {
  "--color-brand-primary": ["--color-action-brand", "--color-text-link-default", "--action-brand-background"],
  "--color-brand-dark": ["--color-action-brand-hover", "--color-text-link-hover", "--action-brand-hover"],
  "--color-action-brand": ["--color-text-link-default", "--action-brand-background"],
  "--color-action-brand-hover": ["--color-text-link-hover", "--action-brand-hover"],
};

function expandVariables(variables: BrandVariables): Record<string, string> {
  const expanded: Record<string, string> = { ...variables };
  for (const [brandVar, derivedVars] of Object.entries(BRAND_CASCADE_MAP)) {
    if (variables[brandVar]) {
      for (const derivedVar of derivedVars) {
        if (!variables[derivedVar]) {
          expanded[derivedVar] = variables[brandVar];
        }
      }
    }
  }
  return expanded;
}

/**
 * Sanitize a CSS variable key or value to prevent injection via <style> tags.
 * Strips characters that could break out of a CSS declaration block.
 */
function sanitizeCSSToken(value: string): string {
  // Strip { } < > which can escape a CSS declaration block or inject HTML
  return value.replace(/[{}<>]/g, "");
}

function variablesToCSSString(variables: Record<string, string>): string {
  return Object.entries(variables)
    .map(([key, value]) => `${sanitizeCSSToken(key)}: ${sanitizeCSSToken(value)};`)
    .join(" ");
}

/**
 * BrandProvider - Apply CSS variable overrides for white-labeling.
 *
 * Wraps children in a container that overrides Edges CSS variables,
 * enabling brand customization. When you override brand variables
 * (like --color-action-brand), this component automatically cascades
 * the override to derived semantic tokens (action colors, link colors, etc.).
 *
 * Pass `darkVariables` to provide separate overrides for dark mode (`.theme-dark`).
 * This is especially useful for ensuring accessible contrast ratios in both modes.
 *
 * Both `variables` and `darkVariables` are applied via stylesheet rules (not inline
 * styles), so dark-mode rules correctly override light-mode rules in `.theme-dark`.
 *
 * @example
 * ```tsx
 * <BrandProvider
 *   variables={{
 *     '--color-action-brand': '#2563eb',
 *     '--color-action-brand-hover': '#1d4ed8',
 *   }}
 *   darkVariables={{
 *     '--color-action-brand': '#60a5fa',
 *     '--color-action-brand-hover': '#93c5fd',
 *   }}
 * >
 *   <App />
 * </BrandProvider>
 * ```
 */
export function BrandProvider({ variables, darkVariables, children }: BrandProviderProps) {
  const rawId = useId();
  // useId produces strings like :r0: — strip colons for use in CSS attribute selectors
  const scopeId = rawId.replace(/:/g, "");

  const hasVariables = variables && Object.keys(variables).length > 0;
  const hasDarkVariables = darkVariables && Object.keys(darkVariables).length > 0;

  if (!hasVariables && !hasDarkVariables) {
    return <>{children}</>;
  }

  const expandedLight = hasVariables ? expandVariables(variables) : null;
  const expandedDark = hasDarkVariables ? expandVariables(darkVariables) : null;

  // Build a combined stylesheet:
  // - Light rules use [data-brand-scope] selector (specificity 0-1-0-0)
  // - Dark rules use .theme-dark [data-brand-scope] (specificity 0-2-0-0)
  // This ensures dark rules always win over light rules in dark mode, since both
  // are author stylesheet rules (unlike inline style which would always win).
  const lightCSS = expandedLight ? `[data-brand-scope="${scopeId}"] { ${variablesToCSSString(expandedLight)} }` : "";
  const darkCSS = expandedDark
    ? `.theme-dark [data-brand-scope="${scopeId}"] { ${variablesToCSSString(expandedDark)} }`
    : "";

  const css = [lightCSS, darkCSS].filter(Boolean).join("\n");

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div data-brand-scope={scopeId}>{children}</div>
    </>
  );
}
